import { useRef, useState, type CSSProperties, type FormEvent, type MouseEvent, type RefObject } from 'react';
import { Link } from 'react-router';
import { LangToggle } from '../App.js';
import { useI18n, type Key } from '../i18n.js';
import { GridBackdrop, Icon, Lockup } from '../ui.js';
import './landing.css';
import { showcase } from './showcase.js';

const IFC_CONTENTS: Key[] = [
  'ifcWalls',
  'ifcDoors',
  'ifcStairs',
  'ifcCurtain',
  'ifcRooms',
  'ifcMaterials',
  'ifcFire',
  'ifcGeo',
  'ifcFurniture',
];

/** What signed-out visitors see at /: the product, what it delivers, and a way to ask for a quote. */
export function Landing() {
  const { t } = useI18n();
  const formStart = useRef<HTMLInputElement>(null);

  const toQuote = (e: MouseEvent) => {
    e.preventDefault();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document
      .getElementById('tilbod')
      ?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    formStart.current?.focus({ preventScroll: true });
  };

  return (
    <div className="landing">
      <header className="l-top">
        <div className="l-in l-top-in">
          <Link to="/" className="brand">
            <Lockup />
          </Link>
          <nav className="l-nav">
            <LangToggle />
            <Link className="button" to="/login">
              {t('signIn')}
            </Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="l-hero">
          <div className="l-grid">
            <GridBackdrop />
          </div>
          <div className="l-in l-hero-in">
            <h1 className="l-title">
              {t('landingTitle')
                .split(/(\S*-\S*)/)
                .map((part, i) =>
                  part.includes('-') ? (
                    <span key={i} className="nobr">
                      {part}
                    </span>
                  ) : (
                    part
                  ),
                )}
            </h1>
            <p className="l-sub">{t('landingSub')}</p>
            <a className="button primary lg" href="#tilbod" onClick={toQuote}>
              {t('requestQuote')}
            </a>
            <ShowcaseFrame />
          </div>
        </section>

        <section className="l-in l-section">
          <h2 className="l-h2">{t('youGet')}</h2>
          <ul className="l-gets">
            <li>
              <Icon.Model className="l-get-icon" width={22} height={22} />
              <h3 className="l-get-name">{t('getIfc')}</h3>
              <div className="l-get-text">
                <p>{t('getIfcText')}</p>
                <ul className="l-tags">
                  {IFC_CONTENTS.map((k) => (
                    <li key={k}>{t(k)}</li>
                  ))}
                </ul>
              </div>
            </li>
            <li>
              <Icon.Viewer className="l-get-icon" width={22} height={22} />
              <h3 className="l-get-name">{t('getViewer')}</h3>
              <p className="l-get-text">{t('getViewerText')}</p>
            </li>
            <li>
              <Icon.Sheet className="l-get-icon" width={22} height={22} />
              <h3 className="l-get-name">{t('getDrawings')}</h3>
              <p className="l-get-text">{t('getDrawingsText')}</p>
            </li>
            <li>
              <Icon.Area className="l-get-icon" width={22} height={22} />
              <h3 className="l-get-name">{t('getAreas')}</h3>
              <p className="l-get-text">{t('getAreasText')}</p>
            </li>
          </ul>
        </section>

        <section className="l-in l-section">
          <h2 className="l-h2">{t('howTitle')}</h2>
          <ol className="l-steps">
            {(
              [
                ['step1', 'step1Text'],
                ['step2', 'step2Text'],
                ['step3', 'step3Text'],
              ] as [Key, Key][]
            ).map(([title, text], i) => (
              <li key={title}>
                <span className="l-bubble" aria-hidden="true">
                  {i + 1}
                </span>
                <h3>{t(title)}</h3>
                <p>{t(text)}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="l-in l-section l-quote" id="tilbod">
          <div className="l-quote-head">
            <h2 className="l-h2">{t('requestQuote')}</h2>
            <p className="muted">{t('quoteLead')}</p>
          </div>
          <QuoteForm firstField={formStart} />
        </section>
      </main>

      <footer className="l-foot">
        <div className="l-in l-foot-in">
          <span className="wordmark" role="img" aria-label="Mynstra" />
          <span className="muted">{t('company')}</span>
          <nav className="l-foot-links">
            <a href="https://mynstra.is" className="l-foot-link">
              mynstra.is
            </a>
            <a href="mailto:hjortur@mynstra.is" className="l-foot-link">
              hjortur@mynstra.is
            </a>
            <Link to="/login" className="l-foot-link">
              {t('signIn')}
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}

/** The showcase slot (./showcase.ts): images side by side at one height, the caption below like a title strip. */
function ShowcaseFrame() {
  const { lang } = useI18n();
  const cols = showcase.images.map((i) => `${(i.width / i.height).toFixed(4)}fr`).join(' ');
  return (
    <figure className="l-show">
      <div className="l-show-images" style={{ '--cols': cols } as CSSProperties}>
        {showcase.images.map((img) => (
          <img key={img.src} src={img.src} width={img.width} height={img.height} alt={img.alt[lang]} />
        ))}
      </div>
      <figcaption>{showcase.caption[lang]}</figcaption>
    </figure>
  );
}

function QuoteForm({ firstField }: { firstField: RefObject<HTMLInputElement | null> }) {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    address: '',
    message: '',
    website: '',
  });
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setState('sending');
    setError(null);
    try {
      const res = await fetch('/api/quote', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ...form, lang }),
      });
      if (res.ok) {
        setState('sent');
        return;
      }
      setState('idle');
      setError(
        res.status === 429 ? t('quoteTooMany') : res.status === 400 ? t('quoteInvalid') : t('quoteFailed'),
      );
    } catch {
      setState('idle');
      setError(t('quoteFailed'));
    }
  }

  if (state === 'sent') {
    return (
      <div className="panel l-thanks" role="status">
        <p className="l-thanks-title">{t('quoteThanks')}</p>
        <p className="muted">{t('quoteReplyTo').replace('{email}', form.email.trim())}</p>
      </div>
    );
  }

  return (
    <form className="panel l-form" onSubmit={submit}>
      <div className="l-form-grid">
        <label>
          {t('quoteName')}
          <input
            ref={firstField}
            required
            maxLength={200}
            autoComplete="name"
            value={form.name}
            onChange={set('name')}
          />
        </label>
        <label>
          {t('email')}
          <input
            type="email"
            required
            maxLength={320}
            autoComplete="email"
            value={form.email}
            onChange={set('email')}
          />
        </label>
        <label>
          {t('quoteCompany')}
          <input maxLength={200} autoComplete="organization" value={form.company} onChange={set('company')} />
        </label>
        <label>
          {t('quoteAddress')}
          <input required maxLength={300} autoComplete="off" value={form.address} onChange={set('address')} />
        </label>
        <label className="l-span">
          {t('quoteMessage')}
          <textarea rows={4} maxLength={4000} value={form.message} onChange={set('message')} />
        </label>
      </div>
      {/* Honeypot: hidden from people and screen readers; bots fill it in. */}
      <div className="l-hp" aria-hidden="true">
        <label>
          Website
          <input tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} />
        </label>
      </div>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <div>
        <button className="primary lg" disabled={state === 'sending'}>
          {state === 'sending' ? t('quoteSending') : t('requestQuote')}
        </button>
      </div>
      <p className="muted small">{t('quotePrivacy')}</p>
    </form>
  );
}
